import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, ChevronDown, ChevronRight, CircleHelp, FileText, Gavel, History, ListChecks, RefreshCw, Sparkles } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api';
import {
  STATE_LABEL,
  STATE_TONE,
  STEPS,
  callAiApi,
  clock,
  errorText,
  isWorking,
  notAvailableText,
  useCallAiSummary,
  type AiSummaryView,
} from '../lib/call-ai';
import type { CallDetail } from '../lib/calls';
import { formatDateTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { Badge, Button, Card, CardHeader, Checkbox, Field, LoadingBlock, Modal, Table, Td, Th, Textarea, Tr, useToast } from './ui';

/**
 * The Call Details page's AI section (the operator's CR, 2026-10-29), beside the recording: the call's
 * summary — title, overview, key points, decisions, action items, open questions — and its full
 * transcript, folded away until asked for. Shown to whoever may read them (connected to the call, or an
 * administrator); its state while the recording is checked, transcribed and summarized; and, for an
 * administrator, why a job failed (our own words) and a new version on request. Everything in it is
 * plain text, each block in the direction of its own words — an Arabic summary reads right to left on an
 * English page, and the other way round.
 */
export function CallAiSummaryCard({ call }: { call: CallDetail }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const canRead = call.can.readAiSummary === true;
  const { full, changed } = useCallAiSummary(call.id, canRead);

  // The status look saw the state move on: the summary is read again, once.
  useEffect(() => {
    if (changed) void queryClient.invalidateQueries({ queryKey: ['calls', 'ai', call.id], exact: true });
  }, [changed, call.id, queryClient]);

  if (!canRead) {
    // Asked, never connected: the recording's own rule, said plainly — only for a call that has one.
    if (call.aiSummary?.eligible && call.aiSummary.refusal === 'NOT_JOINED') {
      return (
        <Card>
          <CardHeader title={<Title />} />
          <p className="text-body-sm text-muted">{t('callAi.notJoined')}</p>
        </Card>
      );
    }
    return null;
  }
  if (full.isLoading) {
    return (
      <Card>
        <CardHeader title={<Title />} />
        <LoadingBlock />
      </Card>
    );
  }
  const view = full.data;
  if (!view || view.state === 'HIDDEN') return null;
  return <SummaryCard call={call} view={view} />;
}

function Title() {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center gap-2">
      <Sparkles className="h-4 w-4 text-primary dark:text-primary-400" aria-hidden />
      {t('callAi.title')}
    </span>
  );
}

function SummaryCard({ call, view }: { call: CallDetail; view: AiSummaryView }) {
  const { t, locale } = useI18n();
  const [regenerating, setRegenerating] = useState(false);
  const s = view.summary;
  const admin = view.admin;
  return (
    <Card>
      {/* Only the short state beside the title: a long title (Arabic's) is never cut for a button. */}
      <CardHeader
        title={<Title />}
        subtitle={t('callAi.disclaimer')}
        action={
          <Badge tone={STATE_TONE[view.state]} dot={isWorking(view)}>
            {t(STATE_LABEL[view.state])}
          </Badge>
        }
      />

      {WORKING.includes(view.state) && <Steps state={view.state} />}
      {view.state === 'RETRY_PENDING' && (
        <p className="mt-3 text-body-sm text-warning-fg dark:text-warning">
          {admin?.job?.nextAttemptAt ? t('callAi.retryAt', { date: formatDateTime(admin.job.nextAttemptAt, locale) }) : t('callAi.retrying')}
        </p>
      )}
      {view.state === 'FAILED' && <p className="text-body-sm text-error-fg dark:text-error-dark">{t('callAi.failed')}</p>}
      {view.state === 'NOT_AVAILABLE' && <p className="text-body-sm text-muted">{t(notAvailableText(view.reason))}</p>}
      {view.state === 'NO_SPEECH' && <p className="text-body-sm text-muted">{t('callAi.noSpeech')}</p>}
      {admin?.job?.errorCode && (view.state === 'FAILED' || view.state === 'RETRY_PENDING' || view.state === 'NOT_AVAILABLE') && (
        <p className="mt-2 rounded-md bg-[var(--surface-sunken)] p-3 text-body-sm text-[var(--text-secondary)]">
          {t(errorText(admin.job.errorCode))}{' '}
          <span className="text-caption text-faint">
            ({admin.job.errorCode} · {t('callAi.attempts', { n: admin.job.attempts, max: admin.job.maxAttempts })})
          </span>
        </p>
      )}

      {s && s.outcome === 'SUMMARY' && (
        <div className="space-y-5">
          {view.pending && (
            <p className="rounded-md bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-[var(--text-secondary)]">
              {t('callAi.newVersionBeingMade', { step: t(STATE_LABEL[view.pending]) })}
            </p>
          )}
          <div>
            <h4 dir="auto" className="text-title text-[var(--text-primary)]">
              {s.title}
            </h4>
            <p dir="auto" className="mt-2 whitespace-pre-line text-body text-[var(--text-secondary)]">
              {s.overview}
            </p>
          </div>
          <Section icon={<ListChecks className="h-4 w-4" aria-hidden />} title={t('callAi.keyPoints')}>
            {s.keyPoints && s.keyPoints.length > 0 ? (
              <ol className="list-decimal space-y-1.5 ps-5 text-body-sm text-[var(--text-primary)]">
                {s.keyPoints.map((p, i) => (
                  <li key={i} dir="auto">
                    {p}
                  </li>
                ))}
              </ol>
            ) : (
              <Empty>{t('callAi.noKeyPoints')}</Empty>
            )}
          </Section>
          <Section icon={<Gavel className="h-4 w-4" aria-hidden />} title={t('callAi.decisions')}>
            {s.decisions && s.decisions.length > 0 ? (
              <ul className="list-disc space-y-1.5 ps-5 text-body-sm text-[var(--text-primary)]">
                {s.decisions.map((d, i) => (
                  <li key={i} dir="auto">
                    {d}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>{t('callAi.noDecisions')}</Empty>
            )}
          </Section>
          <Section icon={<Check className="h-4 w-4" aria-hidden />} title={t('callAi.actionItems')} hint={t('callAi.actionItemsHint')}>
            {s.actionItems && s.actionItems.length > 0 ? (
              <ul className="space-y-2">
                {s.actionItems.map((a, i) => (
                  <li key={i} className="rounded-md border hairline px-3 py-2">
                    <p dir="auto" className="text-body-sm text-[var(--text-primary)]">
                      {a.description}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-caption text-muted">
                      <span>
                        {t('callAi.owner')}: {a.owner ? <bdi>{a.owner}</bdi> : <span className="text-faint">{t('callAi.notSpecified')}</span>}
                      </span>
                      <span>
                        {t('callAi.due')}: {a.dueDate ? <bdi>{a.dueDate}</bdi> : <span className="text-faint">{t('callAi.notSpecified')}</span>}
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>{t('callAi.noActionItems')}</Empty>
            )}
          </Section>
          {s.openQuestions && s.openQuestions.length > 0 && (
            <Section icon={<CircleHelp className="h-4 w-4" aria-hidden />} title={t('callAi.openQuestions')}>
              <ul className="list-disc space-y-1.5 ps-5 text-body-sm text-[var(--text-primary)]">
                {s.openQuestions.map((q, i) => (
                  <li key={i} dir="auto">
                    {q}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          <p className="text-caption text-faint">
            {s.regenerated ? t('callAi.versionUpdated', { n: s.revision, date: formatDateTime(s.createdAt, locale) }) : t('callAi.writtenAt', { date: formatDateTime(s.createdAt, locale) })}
            {admin?.summaryModel && ` · ${admin.summaryModel}`}
            {admin?.transcriptionModel && ` · ${admin.transcriptionModel}`}
          </p>
        </div>
      )}

      {s && s.outcome === 'NO_SPEECH' && view.pending && (
        <p className="mt-2 text-body-sm text-muted">{t('callAi.newVersionBeingMade', { step: t(STATE_LABEL[view.pending]) })}</p>
      )}

      {view.can.regenerate && (
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={() => setRegenerating(true)}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            {t('callAi.regenerate')}
          </Button>
        </div>
      )}

      <TranscriptPanel callId={call.id} view={view} />
      {admin && admin.revisions > 1 && <Revisions callId={call.id} />}
      {regenerating && <RegenerateDialog callId={call.id} hasTranscript={view.transcript.state === 'READY'} onClose={() => setRegenerating(false)} />}
    </Card>
  );
}

const WORKING: AiSummaryView['state'][] = ['WAITING_FOR_RECORDING', 'QUEUED', 'TRANSCRIBING', 'SUMMARIZING'];

/** Waiting for the recording → Queued → Transcribing → Generating summary → Ready, the current step lit. */
function Steps({ state }: { state: AiSummaryView['state'] }) {
  const { t } = useI18n();
  const at = STEPS.indexOf(state);
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-caption" aria-label={t('callAi.progress')}>
      {STEPS.map((step, i) => (
        <li key={step} className="flex items-center gap-2" aria-current={i === at ? 'step' : undefined}>
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 ring-1 ring-inset',
              i < at && 'text-success-fg ring-success/40 dark:text-success',
              i === at && 'bg-primary/10 font-medium text-primary ring-primary/50 dark:text-primary-400',
              i > at && 'text-faint ring-[var(--border-subtle)]',
            )}
          >
            {i < at && <Check className="h-3 w-3" aria-hidden />}
            {i === at && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current motion-reduce:animate-none" aria-hidden />}
            {t(STATE_LABEL[step])}
          </span>
          {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-faint flip-rtl" aria-hidden />}
        </li>
      ))}
    </ol>
  );
}

function Section({ icon, title, hint, children }: { icon: ReactNode; title: string; hint?: string; children: ReactNode }) {
  return (
    <section>
      <h5 className="mb-2 flex items-center gap-2 text-body-sm font-semibold text-[var(--text-primary)]">
        <span className="text-[var(--text-tertiary)]">{icon}</span>
        {title}
      </h5>
      {hint && <p className="-mt-1 mb-2 text-caption text-faint">{hint}</p>}
      {children}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-body-sm text-muted">{children}</p>;
}

/**
 * The full transcript, folded away until asked for — every opening a read the API writes down. Gone with
 * the recording, it says so; the summary above stays.
 */
function TranscriptPanel({ callId, view }: { callId: string; view: AiSummaryView }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const transcript = useQuery({ queryKey: ['calls', 'ai', callId, 'transcript'], queryFn: () => callAiApi.transcript(callId), enabled: open, staleTime: 0, gcTime: 0 });
  if (view.transcript.state === 'NONE') return null;
  if (view.transcript.state === 'EXPIRED') {
    return (
      <p className="mt-5 flex items-start gap-2 rounded-md bg-[var(--surface-sunken)] p-3 text-body-sm text-[var(--text-secondary)]">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-hidden />
        {view.transcript.purgedAt ? t('callAi.transcriptExpiredOn', { date: formatDateTime(view.transcript.purgedAt, locale) }) : t('callAi.transcriptExpired')}
      </p>
    );
  }
  const gone = transcript.error instanceof ApiError && transcript.error.code === 'TRANSCRIPT_EXPIRED';
  return (
    <div className="mt-5 border-t hairline pt-4">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-start text-body-sm font-semibold text-[var(--text-primary)]"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="inline-flex items-center gap-2">
          <FileText className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
          {t('callAi.transcript')}
        </span>
        <ChevronDown className={clsx('h-4 w-4 text-faint transition-transform motion-reduce:transition-none', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <div className="mt-3">
          <p className="mb-3 text-caption text-faint">{t('callAi.transcriptDisclaimer')}</p>
          {transcript.isLoading ? (
            <LoadingBlock />
          ) : gone ? (
            <p className="text-body-sm text-muted">{t('callAi.transcriptExpired')}</p>
          ) : transcript.error ? (
            <p className="text-body-sm text-error-fg dark:text-error-dark">{transcript.error instanceof ApiError ? transcript.error.message : t('common.error')}</p>
          ) : (
            <div className="max-h-[32rem] space-y-4 overflow-y-auto pe-1">
              {transcript.data?.segments.map((seg) => (
                <div key={seg.index}>
                  <p className="mb-1 text-caption text-faint tabular" dir="ltr">
                    {clock(seg.startSeconds)}–{clock(seg.endSeconds)}
                  </p>
                  {seg.text ? (
                    <p dir="auto" className="whitespace-pre-line text-body-sm leading-relaxed text-[var(--text-primary)]">
                      {seg.text}
                    </p>
                  ) : (
                    <p className="text-body-sm text-faint">{t('callAi.transcriptSilent')}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The administrators' list of every version: who asked for it, why, and what it said. */
function Revisions({ callId }: { callId: string }) {
  const { t, locale, localized } = useI18n();
  const [open, setOpen] = useState(false);
  const revisions = useQuery({ queryKey: ['calls', 'ai', callId, 'revisions'], queryFn: () => callAiApi.revisions(callId), enabled: open });
  return (
    <div className="mt-5 border-t hairline pt-4">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-start text-body-sm font-semibold text-[var(--text-primary)]"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="inline-flex items-center gap-2">
          <History className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
          {t('callAi.revisions')}
        </span>
        <ChevronDown className={clsx('h-4 w-4 text-faint transition-transform motion-reduce:transition-none', open && 'rotate-180')} aria-hidden />
      </button>
      {open &&
        (revisions.isLoading ? (
          <LoadingBlock />
        ) : (
          <ul className="mt-3 space-y-3">
            {revisions.data?.map((r) => (
              <li key={r.revision} className="rounded-md border hairline p-3">
                <p className="flex flex-wrap items-center gap-2 text-caption text-muted">
                  <span className="font-medium text-[var(--text-primary)]">{t('callAi.version', { n: r.revision })}</span>
                  {r.isCurrent && <Badge tone="success">{t('callAi.current')}</Badge>}
                  <span>{formatDateTime(r.createdAt, locale)}</span>
                  <span>{r.createdBy ? localized(r.createdBy) : t('callAi.automatic')}</span>
                  <span className="text-faint">{r.model}</span>
                </p>
                {r.reason && (
                  <p className="mt-1 text-caption text-[var(--text-secondary)]">
                    {t('callAi.reason')}: <bdi>{r.reason}</bdi>
                  </p>
                )}
                {r.content ? (
                  <p dir="auto" className="mt-2 text-body-sm text-[var(--text-primary)]">
                    <span className="font-medium">{r.content.title}</span> — {r.content.overview}
                  </p>
                ) : (
                  <p className="mt-2 text-body-sm text-muted">{t('callAi.noSpeech')}</p>
                )}
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}

/** A new version: why (kept with it), and whether the recording is transcribed again first. */
function RegenerateDialog({ callId, hasTranscript, onClose }: { callId: string; hasTranscript: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [retranscribe, setRetranscribe] = useState(!hasTranscript);
  // One key for this dialog: a double click, or a retry after a lost answer, asks once.
  const [requestKey] = useState(() => crypto.randomUUID());
  const send = useMutation({
    mutationFn: () => callAiApi.regenerate(callId, { reason: reason.trim(), requestKey, retranscribe }),
    onSuccess: () => {
      toast.success(t('callAi.regenerateQueued'));
      void queryClient.invalidateQueries({ queryKey: ['calls', 'ai', callId] });
      onClose();
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : t('common.error')),
  });
  const valid = reason.trim().length >= 3 && reason.trim().length <= 500;
  return (
    <Modal
      open
      onClose={() => !send.isPending && onClose()}
      title={t('callAi.regenerateTitle')}
      size="md"
      footer={
        <>
          <Button onClick={onClose} disabled={send.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={send.isPending} disabled={!valid} onClick={() => send.mutate()}>
            <RefreshCw className="h-4 w-4" aria-hidden />
            {t('callAi.regenerate')}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-body-sm text-[var(--text-secondary)]">{t('callAi.regenerateBody')}</p>
      <Field label={t('callAi.reason')} hint={t('callAi.reasonHint')} required>
        <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} autoFocus dir="auto" />
      </Field>
      <div className="mt-4">
        <Checkbox
          checked={retranscribe}
          disabled={!hasTranscript}
          onChange={(e) => setRetranscribe(e.target.checked)}
          label={hasTranscript ? t('callAi.retranscribe') : t('callAi.retranscribeForced')}
        />
      </div>
    </Modal>
  );
}

/** The administrators': who read the summary or the transcript, or asked for a new version — let through or refused. */
export function CallAiAccessLog({ callId }: { callId: string }) {
  const { t, locale, localized } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ['calls', 'ai', callId, 'access'], queryFn: () => callAiApi.access(callId) });
  if (!isLoading && !data?.length) return null;
  return (
    <Card padded={false}>
      <div className="p-5 pb-0">
        <CardHeader title={t('callAi.accessLog')} subtitle={t('callAi.accessLogHint')} />
      </div>
      {isLoading ? (
        <LoadingBlock />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('calls.when')}</Th>
              <Th>{t('calls.person')}</Th>
              <Th>{t('callAi.accessWhat')}</Th>
              <Th>{t('calls.accessFrom')}</Th>
            </tr>
          </thead>
          <tbody>
            {data!.map((row) => (
              <Tr key={row.id}>
                <Td className="whitespace-nowrap text-muted">{formatDateTime(row.createdAt, locale)}</Td>
                <Td>{row.actor ? localized(row.actor) : t('calls.formerEmployee')}</Td>
                <Td>
                  <Badge tone={row.action.endsWith('DENIED') ? 'error' : 'success'}>
                    {t(`callAi.access.${row.action}` as TranslationKey)}
                    {row.reason ? ` · ${t(`callAi.accessReason.${ACCESS_REASONS.has(row.reason) ? row.reason : 'OTHER'}` as TranslationKey)}` : ''}
                  </Badge>
                </Td>
                <Td className="text-muted">
                  {row.channel === 'MOBILE' ? t('calls.viaPhone') : t('calls.viaWeb')}
                  {row.ipAddress && <span dir="ltr"> · {row.ipAddress}</span>}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

const ACCESS_REASONS = new Set(['NOT_A_PARTICIPANT', 'NOT_JOINED', 'NOT_ADMIN', 'NOT_ELIGIBLE', 'EXPIRED', 'NONE', 'JOB_ACTIVE', 'LIMIT', 'UNAVAILABLE']);
